import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, Bot, User, Loader2, Image, Clock, Euro, Phone, Calendar, FileText, HelpCircle, Download, Shield, Zap, Plus, Star, RefreshCw, Wrench, Info, List } from 'lucide-react';

/**
 * Customer Chat Widget Component - Enhanced Version
 * Floating chat bubble with AI bot, quick replies, and image upload
 */
const CustomerChatWidget = ({ caseId, customerToken, customerName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [escalated, setEscalated] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Icon mapping for quick replies
  const iconMap = {
    clock: Clock,
    euro: Euro,
    phone: Phone,
    calendar: Calendar,
    file: FileText,
    help: HelpCircle,
    download: Download,
    shield: Shield,
    zap: Zap,
    plus: Plus,
    star: Star,
    refresh: RefreshCw,
    tool: Wrench,
    info: Info,
    list: List
  };

  // Load chat session when opened
  useEffect(() => {
    if (isOpen && caseId) {
      loadChatSession();
    }
  }, [isOpen, caseId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatSession = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${caseId}`,
        { headers: { 'Authorization': `Bearer ${customerToken}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages(data.messages || []);
        setEscalated(data.escalated || false);
        setQuickReplies(data.quick_replies || []);
      }
    } catch (error) {
      console.error('Load chat failed:', error);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (!file.type.startsWith('image/')) {
      alert('Bitte nur Bilder hochladen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Bild zu groß (max. 5MB)');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage({
        data: e.target.result,
        name: file.name
      });
    };
    reader.readAsDataURL(file);
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async (textOverride = null) => {
    const text = (textOverride || inputText).trim();
    if (!text && !selectedImage) return;
    if (loading) return;

    setInputText('');
    setLoading(true);

    // Optimistic update - show user message immediately
    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      sender: 'customer',
      text: text || '📷 Foto gesendet',
      timestamp: new Date().toISOString(),
      has_image: !!selectedImage
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/send`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${customerToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            text: text || 'Foto gesendet', 
            case_id: caseId,
            image: selectedImage?.data 
          })
        }
      );

      if (res.ok) {
        const data = await res.json();
        
        // Replace temp message and add bot response
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== tempUserMsg.id);
          return [...filtered, data.customer_message, data.bot_response];
        });
        
        if (data.escalated) {
          setEscalated(true);
        }
        
        setSelectedImage(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        // Remove temp message on error
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
        alert('Nachricht konnte nicht gesendet werden');
      }
    } catch (error) {
      console.error('Send failed:', error);
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickReply = (reply) => {
    sendMessage(reply.text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSenderIcon = (sender) => {
    if (sender === 'customer') {
      return <User className="w-4 h-4" />;
    }
    return <Bot className="w-4 h-4" />;
  };

  const getSenderName = (sender) => {
    if (sender === 'customer') return 'Sie';
    if (sender === 'bot') return 'KFZ-Experte (Bot)';
    return 'Gutachter';
  };

  return (
    <>
      {/* Chat Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-brand-green hover:bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50"
          data-testid="chat-bubble"
        >
          <MessageCircle className="w-6 h-6" />
          {messages.some(m => m.sender !== 'customer' && !m.read) && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
              !
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-green to-green-600 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">KFZ-Experte</h3>
                <p className="text-xs text-green-100">
                  {escalated ? 'Mit Gutachter verbunden' : 'Fragen? Ich helfe gerne!'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white p-1"
              data-testid="close-chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-brand-green" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Hallo{customerName ? ` ${customerName}` : ''}!</h4>
                <p className="text-sm text-gray-600">
                  Ich bin Ihr KFZ-Experte. Fragen Sie mich alles rund um Ihr Gutachten, den Ablauf oder die Schadensabwicklung.
                </p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={msg.id || idx}
                className={`flex ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg.sender === 'customer'
                      ? 'bg-brand-green text-white rounded-br-md'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                  }`}
                >
                  {msg.sender !== 'customer' && (
                    <div className="flex items-center gap-1.5 mb-1 text-xs text-gray-500">
                      {getSenderIcon(msg.sender)}
                      <span className="font-medium">{getSenderName(msg.sender)}</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  <p className={`text-[10px] mt-1 ${
                    msg.sender === 'customer' ? 'text-green-100' : 'text-gray-400'
                  }`}>
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Schreibt...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Escalation Banner */}
          {escalated && (
            <div className="bg-amber-50 border-t border-amber-200 px-4 py-2">
              <p className="text-xs text-amber-800">
                <strong>Ein Experte wurde informiert</strong> und wird sich in Kürze bei Ihnen melden.
              </p>
            </div>
          )}

          {/* Quick Replies */}
          {quickReplies.length > 0 && messages.length === 0 && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500 mb-2">Häufige Fragen:</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply) => {
                  const IconComponent = iconMap[reply.icon] || HelpCircle;
                  return (
                    <button
                      key={reply.id}
                      onClick={() => handleQuickReply(reply)}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-700 hover:border-brand-green hover:text-brand-green transition-colors flex items-center gap-1.5"
                      disabled={loading}
                    >
                      <IconComponent className="w-3 h-3" />
                      {reply.text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selected Image Preview */}
          {selectedImage && (
            <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <img 
                  src={selectedImage.data} 
                  alt="Vorschau" 
                  className="w-16 h-16 object-cover rounded-lg border border-gray-300"
                />
                <div className="flex-1">
                  <p className="text-xs text-gray-600 truncate">{selectedImage.name}</p>
                </div>
                <button
                  onClick={removeSelectedImage}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              {/* Image Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-11 h-11 border border-gray-300 hover:border-brand-green text-gray-500 hover:text-brand-green rounded-xl flex items-center justify-center transition-colors"
                disabled={loading}
                title="Foto senden"
              >
                <Image className="w-5 h-5" />
              </button>
              
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ihre Frage eingeben..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green text-sm"
                disabled={loading}
                maxLength={2000}
                data-testid="chat-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={(!inputText.trim() && !selectedImage) || loading}
                className="w-11 h-11 bg-brand-green hover:bg-green-600 disabled:bg-gray-300 text-white rounded-xl flex items-center justify-center transition-colors"
                data-testid="send-message-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerChatWidget;
